import React, { useState } from "react";
import { Link } from "react-router-dom";
import BirthInput from "./BirthInput";
import { Birth, runCalculator } from "../../utils/calculatorApi";
import "./VedicCalculator.css";

type Kind =
  | "mangal_dosha"
  | "kaal_sarp"
  | "sade_sati"
  | "rashi"
  | "nakshatra"
  | "rising_sign"
  | "dasha";

interface Rendered {
  verdict: string;
  tone: "yes" | "no" | "neutral";
  lines: { label: string; value: string }[];
  note: string;
  /** Optional period table, used by the dasha timeline. */
  timeline?: { lord: string; ends: string }[];
}

/* One component for four calculators. They share the same shape: birth details
   in, one API call, a verdict and a few supporting facts out. Splitting them
   into four near-identical files would mean four places to fix every bug.
   engine_text is deliberately never rendered, per section 3 of the API guide. */
const RENDER: Record<Kind, (r: any) => Rendered> = {
  mangal_dosha: (r) => ({
    verdict: r.has_dosha ? "Yes, you are Manglik" : "No, you are not Manglik",
    tone: r.has_dosha ? "yes" : "no",
    lines: [
      { label: "From the Lagna", value: r.from_lagna ? "Yes" : "No" },
      { label: "From the Moon", value: r.from_moon ? "Yes" : "No" },
      { label: "From Venus", value: r.from_venus ? "Yes" : "No" },
    ],
    note: r.has_dosha
      ? "A chart can be Manglik from one reference point and not another, which is why the three rows above can disagree. That is normal, and it is the reason two astrologers sometimes give you different answers."
      : "Mars is not sitting in any of the houses that create the dosha, measured from all three reference points.",
  }),
  kaal_sarp: (r) => ({
    verdict: r.has_dosha
      ? "Yes, Kaal Sarp Dosha is present"
      : "No Kaal Sarp Dosha",
    tone: r.has_dosha ? "yes" : "no",
    lines: [],
    note: r.has_dosha
      ? "Every planet falls on one side of the Rahu to Ketu axis in your chart. It is one factor among many, not a sentence, and how much it matters depends on the rest of the chart."
      : "At least one planet sits outside the Rahu to Ketu axis, so the dosha does not form.",
  }),
  sade_sati: (r) => ({
    verdict: r.is_in_sade_sati
      ? "Yes, you are in Sade Sati right now"
      : "No, you are not in Sade Sati right now",
    tone: r.is_in_sade_sati ? "yes" : "no",
    lines: [
      { label: "Moon sign", value: r.moon_sign ?? "" },
      { label: "Moon house", value: r.moon_house ? String(r.moon_house) : "" },
    ].filter((l) => l.value),
    note: "Sade Sati is Saturn's seven and a half year transit across the sign before your Moon, your Moon sign, and the one after. This answer is worked out against today, so it changes as Saturn moves.",
  }),
  nakshatra: (r) => ({
    verdict: r.nakshatra ? `Your birth star is ${r.nakshatra}` : "Nakshatra",
    tone: "neutral",
    lines: [
      { label: "Nakshatra", value: r.nakshatra ?? "" },
      { label: "Pada", value: r.pada ? String(r.pada) : "" },
      { label: "Ruling planet", value: r.lord ?? "" },
      { label: "Moon sign", value: r.moon_sign ?? "" },
      {
        label: "Moon degree",
        value:
          typeof r.moon_degree === "number" ? `${r.moon_degree.toFixed(2)}°` : "",
      },
    ].filter((l) => l.value),
    note: "Your nakshatra is set by exactly where the Moon sat at birth, dividing the zodiac into 27 rather than 12. The pada narrows it further into one of four quarters, and in many families the first letter of a child's name is chosen from it. The ruling planet is also where your Vimshottari dasha sequence begins.",
  }),
  rising_sign: (r) => ({
    verdict: r.sign ? `Your rising sign is ${r.sign}` : "Rising sign",
    tone: "neutral",
    lines: [
      { label: "Lagna", value: r.sign ?? "" },
      {
        label: "Degree in sign",
        value: typeof r.degree === "number" ? `${r.degree.toFixed(2)}°` : "",
      },
      { label: "Nakshatra", value: r.nakshatra ?? "" },
      { label: "Nakshatra lord", value: r.nakshatra_lord ?? "" },
      { label: "Pada", value: r.nakshatra_pada ? String(r.nakshatra_pada) : "" },
    ].filter((l) => l.value),
    note: "The Lagna is the sign that was climbing over the eastern horizon at the minute you were born. It sets the entire house structure of your chart, which is why it is the one placement that genuinely needs an accurate birth time. It changes roughly every two hours.",
  }),
  dasha: (r) => ({
    verdict: r.current?.mahadasha
      ? `You are running ${r.current.mahadasha}`
      : "Vimshottari dasha",
    tone: "neutral",
    lines: [
      { label: "Mahadasha", value: r.current?.mahadasha ?? "" },
      { label: "Mahadasha ends", value: r.current?.mahadasha_ends ?? "" },
      { label: "Pratyantardasha", value: r.current?.pratyantardasha ?? "" },
      { label: "Sub-period ends", value: r.current?.pratyantardasha_ends ?? "" },
      { label: "Dasha at birth", value: r.birth?.dasha ?? "" },
    ].filter((l) => l.value),
    timeline: Array.isArray(r.timeline) ? r.timeline : undefined,
    note: "Vimshottari is the timing system Vedic astrology runs on. The whole 120 year cycle is divided between the nine planets, and the order and lengths are fixed, so where you start depends entirely on the nakshatra your Moon occupied at birth. The mahadasha is the outer period; the sub-periods inside it are where the detail lives.",
  }),
  rashi: (r) => ({
    verdict: r.sign ? `Your Moon sign is ${r.sign}` : "Moon sign",
    tone: "neutral",
    lines: [
      { label: "Rashi", value: r.sign ?? "" },
      {
        label: "Degree in sign",
        value: typeof r.degree === "number" ? `${r.degree.toFixed(2)}°` : "",
      },
      { label: "House", value: r.house ? String(r.house) : "" },
      { label: "Nakshatra", value: r.nakshatra ?? "" },
      { label: "Nakshatra lord", value: r.nakshatra_lord ?? "" },
    ].filter((l) => l.value),
    note: "This is your Vedic, sidereal Moon sign, which is what people mean by rashi in India. It is often different from the Western sun sign you might know from a magazine, and that is expected rather than an error.",
  }),
};

const VedicCalculator: React.FC<{ kind: Kind; cta: string }> = ({
  kind,
  cta,
}) => {
  const [busy, setBusy] = useState(false);
  const [out, setOut] = useState<Rendered | null>(null);
  const [failed, setFailed] = useState("");

  const run = async (birth: Birth) => {
    setBusy(true);
    setFailed("");
    setOut(null);
    try {
      const result = await runCalculator<any>(kind, birth);
      setOut(RENDER[kind](result));
    } catch (e) {
      setFailed(
        e instanceof Error
          ? e.message
          : "Something went wrong reading your chart."
      );
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="vedcalc">
      <BirthInput onSubmit={run} busy={busy} cta={cta} />

      {failed ? (
        <div className="vedcalc-result vedcalc-failed" role="status">
          <p>{failed}</p>
          <p className="vedcalc-note">
            Check the date, time and place and try once more. If it keeps
            failing, ask Aura AI directly and it will read the chart for you.
          </p>
        </div>
      ) : null}

      {out ? (
        <div className="vedcalc-result" role="status">
          <p className={`vedcalc-verdict vedcalc-${out.tone}`}>{out.verdict}</p>

          {out.lines.length ? (
            <dl className="vedcalc-lines">
              {out.lines.map((l) => (
                <div key={l.label}>
                  <dt>{l.label}</dt>
                  <dd>{l.value}</dd>
                </div>
              ))}
            </dl>
          ) : null}

          {out.timeline?.length ? (
            <div className="vedcalc-timeline">
              <h4>Mahadasha timeline</h4>
              <ol>
                {out.timeline.map((t) => (
                  <li key={`${t.lord}-${t.ends}`}>
                    <span className="vedcalc-lord">{t.lord}</span>
                    <span className="vedcalc-until">until {t.ends}</span>
                  </li>
                ))}
              </ol>
            </div>
          ) : null}

          <p className="vedcalc-note">{out.note}</p>

          <Link to="/chat" className="vedcalc-cta">
            Ask Aura AI what this means for you
          </Link>
        </div>
      ) : null}
    </div>
  );
};

export default VedicCalculator;
